/**
 * One custom React Flow node for the Agent Builder canvas (Phase 2, T2.4).
 *
 * Ported from Agent Studio's components/graph/flowNode.tsx, restyled for
 * factory-ui's light theme + tailwind tokens. Used in BOTH the Build canvas and
 * the Run viewer: `data.status` paints it live during a run; absent => design-time.
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import type { AgentRole, LlmTier, NodeKind, RunStatus, WorkflowNode } from '../../types/agentBuilder';
import { TIER_MODEL } from './llmTiers';

export interface AgentFlowNodeData extends Record<string, unknown> {
  node: WorkflowNode;
  status?: RunStatus;
  confidence?: number;
  selected?: boolean;
  guardrailFail?: boolean;
}

const ROLE_ICON: Record<AgentRole, string> = {
  orchestrator: '◎',
  preprocessor: '▤',
  retriever: '⤓',
  extractor: '⛏',
  analyzer: '📊',
  specialist: '★',
  researcher: '🔎',
  strategist: '♟',
  validator: '✔',
  refiner: '↻',
  critic: '⚖',
  output: '⎙',
};

const KIND_ACCENT: Record<NodeKind, string> = {
  orchestrator: '#6366f1',
  agent: '#0ea5e9',
  guardrail: '#f59e0b',
  validator: '#10b981',
  output: '#8b5cf6',
};

const TIER_LABEL: Record<LlmTier, string> = { premium: 'Premium', mid: 'Mid', cost: 'Cost' };

const STATUS_STYLE: Partial<Record<RunStatus, { ring: string; bg: string; text: string }>> = {
  pending: { ring: '#cbd5e1', bg: '#f1f5f9', text: 'queued' },
  running: { ring: '#6366f1', bg: '#eef2ff', text: 'running' },
  success: { ring: '#10b981', bg: '#ecfdf5', text: 'done' },
  partial: { ring: '#f59e0b', bg: '#fffbeb', text: 'partial' },
  failed: { ring: '#ef4444', bg: '#fef2f2', text: 'failed' },
  skipped: { ring: '#94a3b8', bg: '#f1f5f9', text: 'skipped' },
  awaiting_hitl: { ring: '#f59e0b', bg: '#fffbeb', text: 'needs review' },
  rejected: { ring: '#ef4444', bg: '#fef2f2', text: 'rejected' },
  cancelled: { ring: '#94a3b8', bg: '#f1f5f9', text: 'cancelled' },
};

export function AgentFlowNode({ data }: { data: AgentFlowNodeData }) {
  const { node, status, confidence, selected, guardrailFail } = data;
  const accent = KIND_ACCENT[node.kind] ?? '#0ea5e9';
  const st = status ? STATUS_STYLE[status] : undefined;
  const ring = selected ? '#6366f1' : st?.ring ?? '#e2e8f0';
  const running = status === 'running';
  const awaiting = status === 'awaiting_hitl';
  const isOutput = node.kind === 'output';
  const isHitlCheckpoint = !!node.requiresHitl;
  // A live "running" / "needs review" node gets an attention ring so the canvas
  // animates node-by-node as the run streams (paired with the animated edges).
  const liveRing = running ? st?.ring : awaiting ? st?.ring : undefined;

  return (
    <div
      data-testid={`flow-node-${node.id}`}
      className={`rounded-xl border bg-white px-3.5 py-3 shadow-sm transition-all ${
        running || awaiting ? 'animate-pulse' : ''
      }`}
      style={{
        borderColor: ring,
        minWidth: 184,
        boxShadow: selected
          ? '0 0 0 2px rgba(99,102,241,.35)'
          : liveRing
          ? `0 0 0 3px ${liveRing}55`
          : undefined,
      }}
    >
      {node.kind !== 'orchestrator' && (
        <Handle type="target" position={Position.Left} style={{ background: accent }} />
      )}
      {!isOutput && <Handle type="source" position={Position.Right} style={{ background: accent }} />}

      <div className="flex items-center gap-2">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-sm"
          style={{ background: `${accent}22`, color: accent }}
        >
          {ROLE_ICON[node.role] ?? '◆'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-neutral-800">{node.label}</div>
          <div className="text-[10px] uppercase tracking-wide" style={{ color: accent }}>
            {node.role}
          </div>
        </div>
        {isHitlCheckpoint && (
          <span
            title="Human review checkpoint"
            className="shrink-0 rounded bg-amber-50 px-1 py-0.5 text-[10px] text-amber-600"
          >
            ⚑
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {node.llmTier && (
          <span
            title={TIER_MODEL[node.llmTier]}
            className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-600"
          >
            {TIER_LABEL[node.llmTier]}
          </span>
        )}
        {(node.guardrails?.length ?? 0) > 0 && (
          <span
            className="rounded px-1.5 py-0.5 text-[10px]"
            style={{
              background: guardrailFail ? '#fef2f2' : '#fffbeb',
              color: guardrailFail ? '#ef4444' : '#d97706',
            }}
          >
            ⛨ {node.guardrails!.length}
          </span>
        )}
        {node.refineTo && (
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-600">
            ↻ ×{node.maxIterations ?? 3}
          </span>
        )}
      </div>

      {node.llmTier && (
        <div className="mt-1 truncate font-mono text-[9px] text-neutral-400" title={TIER_MODEL[node.llmTier]}>
          {TIER_MODEL[node.llmTier]}
        </div>
      )}

      {st && (
        <div className="mt-2 flex items-center justify-between">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: st.bg, color: st.ring }}
          >
            {running ? '◍ ' : awaiting ? '⚑ ' : ''}
            {st.text}
          </span>
          {status !== 'pending' && status !== 'running' && (
            <span className="font-mono text-[10px] text-neutral-400">
              {typeof confidence === 'number' && confidence > 0 ? `conf ${confidence.toFixed(2)}` : 'conf —'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export const agentNodeTypes = { agentNode: AgentFlowNode };
